import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { X, Paperclip, ExternalLink, Calendar, User, Flag, Download, FileText, Image as ImageIcon, File as FileIcon, MessageSquare, Send, Trash2, UserPlus, UserMinus, Search } from "lucide-react";

/**
 * Full-height right-side drawer that shows the complete task detail:
 *   • Markdown-rendered instructions with full formatting.
 *   • Attachment previews — image thumbnails inline, PDF/other files as a
 *     downloadable pill. Data-URL and http(s) attachments both work since
 *     the anchor `href` doesn't discriminate.
 *   • Status / priority / deadline / assigner metadata + a "Open workspace"
 *     action so the viewer can jump straight into the collaborative space.
 */
const PRIORITY = {
  high: "border-rose-200 bg-rose-50 text-rose-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-sky-200 bg-sky-50 text-sky-700",
};

const iconFor = (a) => {
  const mime = (a?.mime || "").toLowerCase();
  const kind = (a?.kind || "").toLowerCase();
  if (kind === "image" || mime.startsWith("image/")) return ImageIcon;
  if (kind === "pdf" || mime === "application/pdf") return FileText;
  return FileIcon;
};

export default function TaskDetailDrawer({ task, open, onClose, onOpenWorkspace, onTaskUpdated }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [collabList, setCollabList] = useState([]); // resolved {user_id,name,email} for current watchers
  const [users, setUsers] = useState([]);
  const [collabSearch, setCollabSearch] = useState("");
  const [collabPickUid, setCollabPickUid] = useState("");
  const [collabBusy, setCollabBusy] = useState(false);
  const canEditCollabs = !!user && (
    ["admin", "super_admin"].includes(user.role) ||
    (user.secondary_roles || []).some((r) => ["admin", "super_admin"].includes(r)) ||
    user.user_id === task?.assigner_user_id
  );

  useEffect(() => {
    if (!open || !task?.id) { setComments([]); setBody(""); setCollabList([]); setCollabSearch(""); setCollabPickUid(""); return; }
    api.get(`/employee/tasks/${task.id}/comments`).then((r) => setComments(r.data || [])).catch(() => {});
  }, [open, task?.id]);

  // Resolve collaborator user_ids → names for display.
  useEffect(() => {
    const ids = task?.collaborator_user_ids || [];
    if (!open || !task?.id || ids.length === 0) { setCollabList([]); return; }
    api.get(`/admin/users?role=&q=&page=1&page_size=500`)
      .then((r) => {
        const all = r.data?.items || r.data || [];
        setUsers(all.filter((x) => x.role !== "client"));
        setCollabList(ids.map((uid) => all.find((u) => u.user_id === uid) || { user_id: uid, name: uid }));
      })
      .catch(() => setCollabList(ids.map((uid) => ({ user_id: uid, name: uid }))));
  }, [open, task?.id, (task?.collaborator_user_ids || []).join(",")]);

  // Load users list once when the editor becomes accessible.
  useEffect(() => {
    if (!open || !canEditCollabs || users.length > 0) return;
    api.get(`/admin/users?role=&q=&page=1&page_size=500`)
      .then((r) => setUsers((r.data?.items || r.data || []).filter((x) => x.role !== "client")))
      .catch(() => {});
  }, [open, canEditCollabs]);

  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const r = await api.post(`/employee/tasks/${task.id}/comments`, { body: text });
      setComments((c) => [...c, r.data]);
      setBody("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't post comment"); }
    finally { setBusy(false); }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/employee/tasks/${task.id}/comments/${c.id}`);
      setComments((arr) => arr.filter((x) => x.id !== c.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const patchCollabs = async (nextIds) => {
    setCollabBusy(true);
    try {
      const r = await api.put(`/employee/tasks/${task.id}`, { collaborator_user_ids: nextIds });
      toast.success("Collaborators updated");
      // Optimistically refresh the local list; parent may also refetch.
      setCollabList(nextIds.map((uid) => users.find((u) => u.user_id === uid) || { user_id: uid, name: uid }));
      onTaskUpdated?.(r.data || { ...task, collaborator_user_ids: nextIds });
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setCollabBusy(false); }
  };
  const addCollab = () => {
    if (!collabPickUid) { toast.error("Pick a user first"); return; }
    const current = task.collaborator_user_ids || [];
    if (current.includes(collabPickUid)) { toast.info("Already a collaborator"); return; }
    // Guard: assignee/assigner don't need to be listed as watchers.
    if ([task.assignee_user_id, task.assigner_user_id].includes(collabPickUid)) {
      toast.info("This user is already the assignee or assigner"); return;
    }
    patchCollabs([...current, collabPickUid]);
    setCollabPickUid(""); setCollabSearch("");
  };
  const removeCollab = (uid) => {
    const next = (task.collaborator_user_ids || []).filter((x) => x !== uid);
    patchCollabs(next);
  };

  if (!open || !task) return null;
  const attachments = Array.isArray(task.attachments) ? task.attachments : [];
  const images = attachments.filter((a) => (a.kind || "").toLowerCase() === "image" || (a.mime || "").startsWith("image/"));
  const others = attachments.filter((a) => !((a.kind || "").toLowerCase() === "image" || (a.mime || "").startsWith("image/")));
  return (
    <div className="fixed inset-0 z-[180]" data-testid="task-detail-drawer">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-in slide-in-from-right">
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TASK</div>
            <h2 className="font-serif text-xl text-kb-ink mt-0.5 leading-snug">{task.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] kb-mono uppercase tracking-wider">
              {task.priority && (
                <span className={`px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${PRIORITY[task.priority] || "border-slate-200"}`}>
                  <Flag className="w-3 h-3" /> {task.priority}
                </span>
              )}
              {task.status && <span className="px-1.5 py-0.5 rounded-sm border border-slate-200 text-kb-ink-muted">{task.status}</span>}
              {task.due_date && <span className="text-kb-ink-muted inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {task.due_date}</span>}
              {task.assigner_name && <span className="text-kb-ink-muted inline-flex items-center gap-1"><User className="w-3 h-3" /> {task.assigner_name}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-kb-ink-muted hover:text-kb-ink" data-testid="task-detail-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">INSTRUCTIONS</div>
            {task.description ? (
              <div className="kb-task-md text-sm text-kb-ink leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.description}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-sm text-kb-ink-muted italic">No instructions provided.</div>
            )}
          </section>

          {attachments.length > 0 && (
            <section>
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> ATTACHMENTS ({attachments.length})
              </div>
              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {images.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block border border-slate-200 rounded-sm overflow-hidden group" data-testid={`task-detail-image-${i}`}>
                      <img src={a.url} alt={a.filename || `image-${i}`} className="w-full h-32 object-cover group-hover:scale-[1.02] transition-transform" />
                      <div className="px-2 py-1 text-[10px] kb-mono text-kb-ink-muted truncate">{a.filename || "image"}</div>
                    </a>
                  ))}
                </div>
              )}
              {others.length > 0 && (
                <ul className="space-y-1.5">
                  {others.map((a, i) => {
                    const Icon = iconFor(a);
                    return (
                      <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={`task-detail-file-${i}`}>
                        <Icon className="w-4 h-4 text-kb-ink-muted shrink-0" />
                        <a href={a.url} target="_blank" rel="noreferrer" className="text-kb-primary hover:underline truncate flex-1">
                          {a.filename || a.url}
                        </a>
                        {typeof a.size === "number" && <span className="kb-mono text-[10px] text-kb-ink-light">{(a.size / 1024).toFixed(1)} KB</span>}
                        <a href={a.url} download={a.filename || true} className="text-kb-ink-muted hover:text-kb-primary p-1" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          <section data-testid="task-detail-collaborators">
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">COLLABORATORS (WATCHERS)</div>
            {collabList.length === 0 ? (
              <div className="text-xs text-kb-ink-muted italic">No watchers yet.</div>
            ) : (
              <ul className="space-y-1">
                {collabList.map((c) => (
                  <li key={c.user_id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5" data-testid={`task-collab-${c.user_id}`}>
                    <User className="w-3 h-3 text-kb-ink-light" />
                    <span className="flex-1 truncate">{c.name || c.email || c.user_id}</span>
                    <span className="kb-mono text-[9px] text-kb-ink-light truncate">{c.email || ""}</span>
                    {canEditCollabs && (
                      <button type="button" onClick={() => removeCollab(c.user_id)} disabled={collabBusy} className="p-0.5 text-rose-500 hover:text-rose-700 disabled:opacity-40" title="Remove" data-testid={`task-collab-remove-${c.user_id}`}>
                        <UserMinus className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canEditCollabs && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="task-collab-add-row">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="w-3 h-3 text-kb-ink-light absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    list="task-collab-users"
                    placeholder="Add a watcher…"
                    className="kb-input pl-6 text-xs h-7"
                    value={collabSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      const match = users.find((u) => `${u.name || u.email} — ${u.email}` === val || u.email === val);
                      setCollabSearch(val);
                      setCollabPickUid(match?.user_id || "");
                    }}
                    data-testid="task-collab-input"
                  />
                  <datalist id="task-collab-users">
                    {users.slice(0, 200).map((u) => <option key={u.user_id} value={`${u.name || u.email} — ${u.email}`} />)}
                  </datalist>
                </div>
                <button type="button" onClick={addCollab} disabled={!collabPickUid || collabBusy} className="kb-btn-primary text-[10px] h-7 px-2 disabled:opacity-40" data-testid="task-collab-add-btn">
                  <UserPlus className="w-3 h-3" /> Add
                </button>
              </div>
            )}
          </section>

          <section data-testid="task-detail-comments">
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2 inline-flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> COMMENTS ({comments.length})
            </div>
            {comments.length === 0 ? (
              <div className="text-xs text-kb-ink-muted italic">No comments yet — ask a clarifying question below.</div>
            ) : (
              <ul className="space-y-2">
                {comments.map((c) => (
                  <li key={c.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={`task-comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-kb-ink">{c.user_name || c.user_id}</span>
                      <span className="kb-mono text-[9px] text-kb-ink-light">{c.user_role}</span>
                      <span className="kb-mono text-[9px] text-kb-ink-light ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                      {(user?.user_id === c.user_id || ["admin", "super_admin"].includes(user?.role)) && (
                        <button onClick={() => removeComment(c)} className="text-rose-500 hover:text-rose-700" title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="kb-task-md text-kb-ink">
                      <CollapsibleCommentBody body={c.body} testid={`task-comment-body-${c.id}`} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={submit} className="mt-3 flex gap-2" data-testid="task-detail-comment-form">
              <input
                type="text"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Ask a clarifying question or leave a note (markdown supported)…"
                className="kb-input flex-1 text-sm"
                data-testid="task-detail-comment-input"
              />
              <button type="submit" disabled={busy || !body.trim()} className="kb-btn-primary text-xs inline-flex items-center gap-1" data-testid="task-detail-comment-submit">
                <Send className="w-3 h-3" /> {busy ? "…" : "Post"}
              </button>
            </form>
          </section>
        </div>

        <footer className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="kb-btn-outline text-xs">Close</button>
          {onOpenWorkspace && (
            <button onClick={() => onOpenWorkspace(task)} className="kb-btn-primary text-xs inline-flex items-center gap-1" data-testid="task-detail-open-workspace">
              Open workspace <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
}


/** Collapse long comment bodies (>320 chars or >6 lines) behind a
 *  "Show more" toggle so the drawer stays scannable. Falls back to inline
 *  rendering for short comments so we don't add noise. */
function CollapsibleCommentBody({ body, testid }) {
  const [open, setOpen] = React.useState(false);
  const text = body || "";
  const lines = text.split("\n").length;
  const long = text.length > 320 || lines > 6;
  const shown = open || !long ? text : text.slice(0, 320);
  return (
    <div data-testid={testid}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{shown}</ReactMarkdown>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline mt-1"
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
